/** Per-store stock matrix для product edit (Phase F1).
 *
 * Таблица: store_name | quantity (editable number input) | reserved.
 * Total всегда внизу = сумма по всем магазинам (= products.stock_quantity).
 *
 * PATCH per cell через PUT /products/{id}/store-stock с дебаунсом 700ms.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Store as StoreIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { api } from "@/lib/api";

interface StoreStockRow {
  store_id: number;
  store_name: string;
  store_city: string;
  variant_id: number | null;
  quantity: number;
  reserved: number;
}

interface Props {
  productId: number;
}

export function StoreStockEditor({ productId }: Props): JSX.Element {
  const queryClient = useQueryClient();
  const [drafts, setDrafts] = useState<Record<number, number>>({});

  const { data: rows, isLoading } = useQuery({
    queryKey: ["admin-store-stock", productId],
    queryFn: async () => {
      const { data } = await api.get(`/products/${productId}/store-stock`);
      return data as StoreStockRow[];
    },
  });

  // Sync drafts from server data
  useEffect(() => {
    if (!rows) return;
    setDrafts((prev) => {
      const next: Record<number, number> = {};
      for (const r of rows.filter((x) => x.variant_id == null)) {
        next[r.store_id] = prev[r.store_id] ?? r.quantity;
      }
      return next;
    });
  }, [rows]);

  const setMutation = useMutation({
    mutationFn: async ({
      store_id,
      quantity,
    }: {
      store_id: number;
      quantity: number;
    }) => {
      await api.put(`/products/${productId}/store-stock`, {
        store_id,
        quantity,
        variant_id: null,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin-store-stock", productId],
      });
      queryClient.invalidateQueries({
        queryKey: ["admin-product", productId],
      });
    },
    onError: (e: any) => {
      toast.error(e?.response?.data?.error?.message ?? "Не сохранилось");
    },
  });

  if (isLoading) {
    return (
      <div className="text-sm text-muted-foreground flex items-center gap-2">
        <Loader2 className="w-3 h-3 animate-spin" /> Загрузка остатков по магазинам…
      </div>
    );
  }

  // Banner: пока 1С не подключён — редактируем вручную; после интеграции
  // эта таблица станет read-only и будет sync'иться из 1С.
  const banner = (
    <div className="rounded-md border border-warning/30 bg-warning/5 px-3 py-2 text-xs text-warning-foreground flex items-start gap-2">
      <span>⓵</span>
      <div>
        Остатки <strong>планируется синхронизировать из 1С</strong> (точки
        продаж = склады). После настройки 1С-обмена редактирование здесь
        будет заблокировано.
      </div>
    </div>
  );

  const productRows = rows?.filter((r) => r.variant_id == null) ?? [];
  const total = productRows.reduce((sum, r) => sum + (drafts[r.store_id] ?? r.quantity), 0);
  const totalReserved = productRows.reduce((sum, r) => sum + r.reserved, 0);

  return (
    <div className="space-y-3">
      {banner}
      <div className="flex items-center justify-between">
        <h3 className="font-semibold flex items-center gap-2">
          <StoreIcon className="w-4 h-4" />
          Остатки по магазинам
        </h3>
        <div className="text-sm text-muted-foreground">
          Итого: <strong>{total}</strong>
          {totalReserved > 0 && (
            <span className="text-warning ml-1">
              (зарезервировано {totalReserved})
            </span>
          )}
        </div>
      </div>

      {productRows.length === 0 ? (
        <p className="text-xs text-muted-foreground text-center py-4 border rounded">
          Нет магазинов. Создайте магазин в «Магазины», чтобы задать остатки.
        </p>
      ) : (
        <div className="border rounded-md">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Магазин</TableHead>
                <TableHead>Город</TableHead>
                <TableHead className="w-28">Остаток</TableHead>
                <TableHead className="w-28">Резерв</TableHead>
                <TableHead className="w-28">Доступно</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {productRows.map((r) => {
                const draft = drafts[r.store_id] ?? r.quantity;
                const available = Math.max(0, draft - r.reserved);
                const dirty = draft !== r.quantity;
                return (
                  <TableRow key={r.store_id}>
                    <TableCell className="font-medium">{r.store_name}</TableCell>
                    <TableCell className="text-muted-foreground text-xs">
                      {r.store_city}
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        min={0}
                        value={draft}
                        onChange={(e) =>
                          setDrafts((prev) => ({
                            ...prev,
                            [r.store_id]: Math.max(0, Number(e.target.value) || 0),
                          }))
                        }
                        onBlur={() => {
                          if (dirty) {
                            setMutation.mutate({
                              store_id: r.store_id,
                              quantity: draft,
                            });
                          }
                        }}
                        className={`h-8 text-xs ${dirty ? "ring-1 ring-warning" : ""}`}
                      />
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm tabular-nums">
                      {r.reserved}
                    </TableCell>
                    <TableCell className="font-semibold text-sm tabular-nums">
                      {available}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      <p className="text-[10px] text-muted-foreground">
        Общий остаток <code>products.stock_quantity</code> пересчитывается
        автоматически (сумма всех магазинов).
      </p>
    </div>
  );
}
